"use client";

import { createMapLink } from "@gettactic/helpers/src/navigation/createMapLink";
import { Map as MapIcon, SquareArrowOutUpRight } from "lucide-react";
import React, { useRef } from "react";
import { MapViewEmbedded } from "@/features/map/components/MapView/MapViewEmbedded";
import { StaticMapPinLayer } from "@/features/map/components/MapView/StaticMapPinLayer";

interface LocationOnMapProps {
  officeId: string;
  /** Floor to render; until it resolves the card shows a loading placeholder. */
  floorId: string | null;
  /** Resource to highlight on the floor, when the location is a resource. */
  resourceId?: string | null;
  /** Pinned point on the floor, when the location is a coordinate. */
  point?: { x: number; y: number } | null;
  officeName: string;
  floorName?: string | null;
  /** Rendered inside the card, above the map — e.g. a "From · Desk 34" strip. */
  header?: React.ReactNode;
}

/**
 * Embedded, read-only floor map card with an "Office · Floor" footer and a link
 * to the full map. Purely presentational: callers resolve which office, floor,
 * resource or pin to show. Shared by the workplace-request and reservation
 * detail modals.
 */
export const LocationOnMap: React.FC<LocationOnMapProps> = ({
  officeId,
  floorId,
  resourceId,
  point,
  officeName,
  floorName,
  header
}) => {
  const mapWrapperRef = useRef<HTMLDivElement | null>(null);

  const footerLabel = [officeName, floorName].filter(Boolean).join(" · ");
  const mapHref = createMapLink({
    date: new Date().toISOString(),
    officeId,
    resourceId: resourceId ?? undefined,
    floorId: floorId ?? undefined
  });

  return (
    <section>
      <div className="overflow-hidden rounded-xl border border-neutral-200">
        {header}
        <div className="relative h-[202px] w-full bg-neutral-50">
          {floorId ? (
            point ? (
              <div ref={mapWrapperRef} className="relative h-full w-full">
                <MapViewEmbedded
                  officeId={officeId}
                  floorId={floorId}
                  enableControls={false}
                  showMiniMap={false}
                />
                <StaticMapPinLayer
                  mapWrapperRef={mapWrapperRef}
                  point={{ x: point.x, y: point.y }}
                />
              </div>
            ) : (
              <MapViewEmbedded
                officeId={officeId}
                floorId={floorId}
                focussedResourceId={resourceId ?? undefined}
                enableControls={false}
                showMiniMap={false}
              />
            )
          ) : (
            <div className="flex h-full items-center justify-center text-neutral-400 text-sm">
              Loading map…
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <span className="flex min-w-0 items-center gap-2 text-neutral-700 text-sm">
            <MapIcon className="h-4 w-4 shrink-0 text-neutral-400" />
            <span className="truncate">{footerLabel}</span>
          </span>
          <a
            href={mapHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex shrink-0 items-center gap-1.5 font-medium text-neutral-900 text-sm hover:text-neutral-600"
          >
            View on map
            <SquareArrowOutUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
};
